import { Link } from "react-router-dom";
import { loadRun, PHASES, TOTAL_STEPS } from "../lib/runState";
import { toolsInCollection } from "../toolRegistry";
import { useOrgCollections } from "../lib/useOrgProfiles";
import Avatar from "../components/ui/Avatar";


export default function HomePage({ username }: { username: string }) {
  const run = loadRun();
  // Put the signed-in person's own toolset first -- it is the one they want.
  const firstName = (username ?? "").trim().split(" ")[0].toLowerCase();
  const liveCollections = useOrgCollections();
  const orderedCollections = [...liveCollections].sort((a, b) => {
    const am = firstName && a.person.toLowerCase().startsWith(firstName) ? 0 : 1;
    const bm = firstName && b.person.toLowerCase().startsWith(firstName) ? 0 : 1;
    return am - bm;
  });
  const complete = run.step > TOTAL_STEPS;
  const pct = Math.min(((run.step - 1) / TOTAL_STEPS) * 100, 100);

  return (
    <div>
      <h2 className="font-extrabold text-[26px] tracking-tight mb-1">
        {new Date().getHours() < 12 ? "Good morning" : "Good afternoon"}, {username.split(" ")[0]}
      </h2>
      <p className="text-ink-soft mb-6">Here's where district operations stand.</p>

      <div className="grid grid-cols-1 min-[880px]:grid-cols-[1.55fr_1fr] gap-4 mb-6">
        {/* Active run banner */}
        <Link to="/workflows/adult-control" className="ui-card border-l-4 !border-l-wnv p-5 hover:shadow-lg2 transition block">
          <div className="flex items-start justify-between gap-3">
            <div>
              <span className="pill bg-wnv-tint text-wnv">Active response</span>
              <div className="font-extrabold text-[18px] mt-1.5">Adult Control Run</div>
              <div className="text-ink-soft text-[13px]">Weekly adult mosquito control response</div>
            </div>
            <div className="text-right shrink-0">
              <div className="eyebrow">Deadline</div>
              <div className="text-wnv font-bold text-sm">ULV Thu 3:00 PM</div>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <div className="h-2 bg-paper-2 rounded-full overflow-hidden flex-1">
              <div className="h-full bg-brand rounded-full" style={{ width: `${pct}%` }} />
            </div>
            <span className="font-mono text-xs">{complete ? "Done" : `Phase ${run.step} / ${TOTAL_STEPS}`}</span>
          </div>
          <div className="mt-3 flex flex-wrap gap-1.5">
            {PHASES.map((p) => {
              const st = p.num < run.step ? "done" : p.num === run.step ? "active" : "future";
              return (
                <span
                  key={p.num}
                  className={`pill text-[11px] ${
                    st === "done"
                      ? "bg-brand-tint text-brand-deep"
                      : st === "active"
                      ? "border border-brand text-brand-deep"
                      : "bg-paper-2 text-ink-faint"
                  }`}
                >
                  {st === "done" ? "✓ " : ""}
                  {p.title.split(" ")[0].replace(/[&—]/g, "")}
                </span>
              );
            })}
          </div>
        </Link>

        {/* Surveillance snapshot */}
        <div className="ui-card p-5">
          <div className="flex items-baseline justify-between mb-3">
            <h3 className="font-extrabold text-[15px]">Surveillance</h3>
            <span className="font-mono text-xs text-ink-faint">2026 season</span>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="rounded-sm2 bg-wnv-tint p-3 text-center">
              <div className="font-extrabold text-[26px] text-wnv leading-none">—</div>
              <div className="eyebrow !text-wnv mt-1.5">WNV+</div>
            </div>
            <div className="rounded-sm2 bg-paper-2 p-3 text-center">
              <div className="font-extrabold text-[26px] leading-none">—</div>
              <div className="eyebrow mt-1.5">Tested</div>
            </div>
            <div className="rounded-sm2 bg-sle-tint p-3 text-center">
              <div className="font-extrabold text-[26px] text-sle leading-none">—</div>
              <div className="eyebrow !text-sle mt-1.5">SLE+</div>
            </div>
          </div>
          <p className="text-ink-faint text-xs mt-3">Live counts wire to the disease layer in a later phase.</p>
        </div>
      </div>

      {/* Toolsets: pick whose set of tools you want, then drill in. */}
      <div className="flex items-baseline gap-2 mb-3">
        <h3 className="font-extrabold text-[15px]">Toolsets</h3>
        <Link to="/tools" className="text-brand text-[13px] font-semibold hover:underline ml-auto">
          All tools →
        </Link>
      </div>
      <div className="grid grid-cols-1 min-[560px]:grid-cols-2 min-[980px]:grid-cols-3 gap-3 mb-6">
        {orderedCollections.map((c, i) => {
          const tools = toolsInCollection(c);
          const mine = Boolean(firstName) && c.person.toLowerCase().startsWith(firstName);
          return (
            <Link
              key={c.id}
              to={`/tools/group/${c.id}`}
              className="ui-card p-4 h-full hover:shadow-lg2 hover:-translate-y-0.5 transition duration-150 animate-[fadeUp_.35s_ease-out_both]"
              style={{ animationDelay: `${Math.min(i, 8) * 50}ms` }}
            >
              <div className="flex items-center gap-3">
                <Avatar
                  initials={c.initials}
                  accent={c.accent}
                  photo={c.photo}
                  alt={c.person}
                  size="lg"
                  shape="rounded"
                />
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-sm truncate">{c.label}</div>
                  <div className="text-ink-faint text-xs">
                    {tools.length === 0
                      ? "Nothing assigned yet"
                      : `${tools.length} tool${tools.length === 1 ? "" : "s"}`}
                  </div>
                </div>
                {mine ? <span className="pill bg-brand-tint text-brand-deep shrink-0">You</span> : null}
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
